import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { sendStepCompleteEmail } from '../../../UtilsEmail';

import {
  trackUploadSample,
  trackClickUploadMore,
  trackClipsDeleteClip,
} from '../../../analytics';

import { SellingPoint, InfoPoints } from '../../../components/custom';
import { Alert, ProgressBar } from '../../../components/general';
import {
  PageWrapper,
  PageHeader,
  BackBtn,
} from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import useSampleProjectData from '../../../hooks/useSampleProjectData';

import { getCreationResults } from '../../../services/api/creations';
import { getClipFiles, fileDelete } from '../../../services/api/files';
import { queuesDelete } from '../../../services/api/queues';

import { useProjectStore } from '../../../stores/project';
import { useUserStore } from '../../../stores/user';
import { useVideoBuilderStore } from '../../../stores/videoBuilder';

import { File, PageError, ModalError, UploadingFile } from '../../../types';
import { isEmpty } from '../../../utils';

import DoneBtn from '../components/doneBtn';
import FileDropArea from '../components/fileDropArea';
import MobileReminderBox from '../components/mobileReminderBox';
import PageWithSideBar from '../components/pageWithSideBar';
import ThumbnailsContainer from '../components/thumbnailsContainer';
import { UploadStage, UploadingClips } from '../components/types';
import UploadMoreBtn from '../components/uploadMoreBtn';
import getClipsText from './components/getClipsText';
import SampleComponent from './components/sampleComponent';

const Clips = () => {
  const { eventId = '' } = useParams();

  const { user } = useUserStore();
  const {
    eventId: eventIdLocal,
    currentProject,
    projectError,
    setEventId,
    setSampleProject,
  } = useProjectStore();
  const { updateDeletingClipsIds } = useVideoBuilderStore();

  const [uploadedClips, setUploadedClips] = useState<File[]>([]);
  const [uploadingClips, setUploadingClips] = useState<UploadingClips>({});
  const [hasCreations, setHasCreations] = useState(false);

  const [showUpload, setShowUpload] = useState(false); // whether upload area is shown (user clicked "upload more")
  const [uploadingCount, setUploadingCount] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState<ModalError>(null);

  const uploadRef = useRef<React.ElementRef<typeof Upload>>(null);

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);
  const sampleClips = sampleProjectData.clips || [];

  // assignee data
  const assigneeData = JSON.parse(localStorage.getItem('assigneeData') ?? '{}');
  const isPublic = currentProject?.isAssigneeAccess ?? false;

  // set event id on mount
  useEffect(() => {
    if (isSample) setSampleProject(eventId);
    else if (eventIdLocal !== eventId) setEventId(eventId, true);
  }, [eventId, eventIdLocal, isSample, setSampleProject, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      if (
        currentProject?.isAssigneeAccess &&
        !assigneeData[currentProject?.eventId]?.accessPages.includes('clips')
      ) {
        setPageError(403);
        setIsLoading(false);
        return;
      }

      await fetchClipsData();
      await fetchCreationsData();

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
      return;
    }

    if (currentProject) {
      if (isSample) {
        if (sampleProjectData.isClipsUploaded) setUploadedClips(sampleClips);
        setIsLoading(false);
      } else onMount();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, projectError]);

  const fetchClipsData = async () => {
    try {
      const clips = await getClipFiles(eventId, isPublic);
      setUploadedClips(clips);
    } catch (err) {
      console.error(`Error fetching the clips: ${err}`);
      setError({ subheading: 'Error fetching the clips' });
    }
  };

  const fetchCreationsData = async () => {
    try {
      const creations = await getCreationResults(eventId, true);
      setHasCreations(!isEmpty(creations));
    } catch (err) {
      console.error(`Error fetching the creations: ${err}`);
      setError({ subheading: 'Error fetching the creations' });
    }
  };

  // compute current stage: to-upload | uploading | summary
  const stage: UploadStage = useMemo(() => {
    if (!isEmpty(uploadingClips)) return 'uploading';
    if (isEmpty(uploadedClips) || showUpload) return 'to-upload';
    return 'summary';
  }, [uploadedClips, uploadingClips, showUpload]);

  const prevStageRef = useRef(stage);

  const handleDropAll = (fileList: UploadFile[]) => {
    setUploadingCount(fileList.length);
  };

  const handleDrop = (file: UploadFile) => {
    setShowUpload(false);

    setUploadingClips((prevClips) => ({
      ...prevClips,
      [file.uid]: { id: file.uid, name: file.name, progress: 1 },
    }));
  };

  const handleProgress = (file: UploadingFile) => {
    setUploadingClips((prevClips) => ({ ...prevClips, [file.id]: file }));
  };

  const handleComplete = async (file: UploadFile) => {
    await fetchClipsData();

    // remove the clip from uploading list
    setUploadingClips((prevClips) => {
      const uploadingClipsTemp = { ...prevClips };
      delete uploadingClipsTemp[file.uid];
      return uploadingClipsTemp;
    });
  };

  const handleError = (file: UploadFile) => {
    if (file.type?.startsWith('image/'))
      setError({
        subheading: `${file.name} file upload failed. Ensure the file is in .MP4 or .MOV video format.`,
      });
    else
      setError({
        heading: 'Oops!',
        subheading: `It looks like there's an issue with your upload due to connectivity problems. We recommend refreshing the page and uploading your clips in small batches.`,
      });
  };

  // when upload completes
  useEffect(() => {
    const prevStage = prevStageRef.current;

    if (prevStage === 'uploading' && stage === 'summary') {
      toast.success('All clips are uploaded!', {
        toastId: 'clips-upload-success',
      });

      localStorage.setItem(
        'uploadData',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('uploadData') ?? '{}'),
          clipsCount: (isSample ? sampleClips : uploadedClips).length,
        }),
      );
      window.dispatchEvent(new Event('storage'));

      // for sample – track clips upload with num of selected clips
      if (isSample) trackUploadSample('clips', sampleClips.length);
      // send email to the user that the upload is complete
      else if (!isPublic && user && currentProject)
        sendStepCompleteEmail(user, currentProject, "Clips' upload");
    }

    prevStageRef.current = stage;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // deletes file and queues associated with it
  const handleDelete = async (video: File) => {
    try {
      // add elementId to list of deleting clips
      updateDeletingClipsIds(video.elementId, true);

      await queuesDelete(video.elementId, isPublic);
      await fileDelete(video.elementId, isPublic);
      await fetchClipsData();

      trackClipsDeleteClip();
    } catch {
      toast.error(`Delete failed for ${video.name}. Try again later`);
    } finally {
      // remove elementId from list of deleting clips
      updateDeletingClipsIds(video.elementId);
    }
  };

  const handleUploadMore = (
    e: React.MouseEvent<HTMLElement>,
    position: string,
  ) => {
    setShowUpload(true);
    setTimeout(() => uploadRef.current?.upload?.uploader?.onClick(e));
    trackClickUploadMore('clips', position, isSample);
  };

  const { subheading, progress, infoPoints } = getClipsText({
    stage,
    uploadingClips,
    uploadingCount,
    clipsCount: uploadedClips.length,
    orientation: currentProject?.videoMode,
    isSample,
    handleUploadMore,
    isPublic,
  });

  return (
    <PageWrapper
      type={isPublic ? 'header-only' : 'project'}
      title={`Clips – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
    >
      <BackBtn label="Video Builder" />

      <PageWithSideBar
        mainContent={
          <>
            <PageHeader heading="Clips" subheading={subheading} />

            {/* // to upload view */}
            {stage === 'to-upload' && (
              <React.Fragment>
                <SampleComponent
                  setUploadedClips={setUploadedClips}
                  setUploadingClips={setUploadingClips}
                  setUploadingCount={setUploadingCount}
                />

                <FileDropArea
                  type="clips"
                  uploadRef={uploadRef}
                  handleDropAll={handleDropAll}
                  onDrop={handleDrop}
                  onProgress={handleProgress}
                  onComplete={handleComplete}
                  onError={handleError}
                  isSample={isSample}
                />
              </React.Fragment>
            )}

            {/* uploading view */}
            {stage === 'uploading' && (
              <div className="flex flex-col gap-4 text-center">
                <ProgressBar progress={progress} />

                <div className="font-bold uppercase">Uploading</div>
              </div>
            )}

            {/* summary view */}
            {stage === 'summary' && (
              <React.Fragment>
                <ThumbnailsContainer
                  videos={uploadedClips}
                  handleDelete={handleDelete}
                  isBig
                  isSample={isSample}
                />

                <div className="flex flex-col sm:flex-row gap-4">
                  <DoneBtn addClass={!isSample ? 'sm:w-1/2' : ''} />

                  {!isSample && (
                    <UploadMoreBtn
                      onClick={(e: React.MouseEvent<HTMLElement>) =>
                        handleUploadMore(e, 'Bottom button')
                      }
                    >
                      Upload More
                    </UploadMoreBtn>
                  )}
                </div>
              </React.Fragment>
            )}
          </>
        }
        sideContent={
          <>
            {hasCreations && (
              <Alert generatedAlertText="If you upload new clips, it will only be used in videos for new people. All generated videos will remain the same." />
            )}

            {stage !== 'summary' && !isSample && (
              <SellingPoint text="Try uploading in small batches to avoid any issues." />
            )}

            <InfoPoints points={infoPoints} isInfo={stage === 'summary'} />

            {!isSample && !isPublic && <MobileReminderBox />}
          </>
        }
        sampleTipType={isSample ? stage : undefined}
      />

      <ErrorModal
        show={!!error}
        heading={error?.heading ?? 'Something went wrong'}
        subheading={error?.subheading}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default Clips;
